import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { User } from 'oidc-client-ts';
import { useI18n } from '../i18n';
import { CALLBACK_PATH, userManager } from './oidc';
import { cleanReturnTo } from './return-to';
import { rememberStop, signInHints } from './sign-in-stop';

export interface Me {
  id: string;
  email: string;
  name: string;
  emailVerified: boolean;
}

interface AuthState {
  me: Me | null;
  ready: boolean;
  /** To Keycloak, in the app's language (or `hints.language`), the e-mail filled in when given (TF-239). */
  signIn: (returnTo?: string, hints?: { email?: string | null; language?: string | null }) => Promise<void>;
  signOut: () => Promise<void>;
  /** Forget the session locally without the round trip to Keycloak (after deleting one's account). */
  forget: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

const toMe = (user: User | null): Me | null =>
  user && !user.expired
    ? {
        id: user.profile.sub,
        email: user.profile.email ?? '',
        name: user.profile.name ?? user.profile.preferred_username ?? user.profile.email ?? '',
        emailVerified: user.profile.email_verified === true,
      }
    : null;

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let active = true;
    const finish = (loaded: User | null) => {
      if (!active) return;
      setUser(loaded);
      setReady(true);
    };
    if (location.pathname === CALLBACK_PATH) {
      // The callback page exchanges the code; the userLoaded event below then brings the user.
      finish(null);
    } else {
      userManager.getUser().then((stored) => finish(stored && !stored.expired ? stored : null), () => finish(null));
    }
    const loaded = (next: User) => setUser(next);
    const unloaded = () => setUser(null);
    userManager.events.addUserLoaded(loaded);
    userManager.events.addUserUnloaded(unloaded);
    userManager.events.addSilentRenewError(unloaded);
    return () => {
      active = false;
      userManager.events.removeUserLoaded(loaded);
      userManager.events.removeUserUnloaded(unloaded);
      userManager.events.removeSilentRenewError(unloaded);
    };
  }, []);

  const { language } = useI18n();
  const signIn = useCallback(
    (returnTo?: string, hints?: { email?: string | null; language?: string | null }) =>
      userManager.signinRedirect({
        state: { returnTo: cleanReturnTo(returnTo ?? location.pathname + location.search) },
        extraQueryParams: signInHints({ language: hints?.language ?? language, email: hints?.email }),
      }),
    [language],
  );
  // Signed out on purpose: the start page then says so instead of going straight back to Keycloak.
  const signOut = useCallback(() => {
    rememberStop('signedOut');
    return userManager.signoutRedirect();
  }, []);
  const forget = useCallback(async () => {
    rememberStop('signedOut');
    await userManager.removeUser();
    setUser(null);
  }, []);

  const value = useMemo<AuthState>(() => ({ me: toMe(user), ready, signIn, signOut, forget }), [user, ready, signIn, signOut, forget]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const state = useContext(AuthContext);
  if (!state) throw new Error('useAuth outside AuthProvider');
  return state;
}

/** For pages behind sign-in, where `me` is guaranteed. */
export function useMe(): Me {
  const { me } = useAuth();
  if (!me) throw new Error('useMe on a signed-out page');
  return me;
}
